import React, { useRef, useEffect } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";

interface InlineEditPopoverProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  value: string | number;
  type?: string;
  onSubmit: (value: string) => void;
  onCancel: () => void;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
  children: React.ReactNode;
  renderContent?: (args: { inputRef: React.RefObject<any> }) => React.ReactNode;
}

export const InlineEditPopover: React.FC<InlineEditPopoverProps> = ({
  open,
  onOpenChange,
  value,
  type = "text",
  onSubmit,
  onCancel,
  inputProps = {},
  children,
  renderContent,
}) => {
  const inputRef = useRef<any>(null);

  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
      if (typeof inputRef.current.select === 'function') inputRef.current.select();
    }
  }, [open]);

  function handleFormSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (inputRef.current) {
      onSubmit(inputRef.current.value);
      onOpenChange(false); // Close the popover after submit
    }
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Escape") {
      onCancel();
    }
  }

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent
        align="center"
        className="p-2 bg-white dark:bg-muted border dark:border-gray-700 rounded shadow-md z-50 min-w-[120px]"
      >
        {renderContent ? (
          <form onSubmit={handleFormSubmit}>
            {renderContent({ inputRef })}
          </form>
        ) : (
          <form onSubmit={handleFormSubmit}>
            <input
              ref={inputRef}
              type={type}
              defaultValue={value}
              onKeyDown={handleKeyDown}
              className="w-full px-2 py-1 border rounded focus:outline-none focus:ring-2 focus:ring-primary dark:bg-muted dark:text-white dark:border-gray-700"
              autoFocus
              {...inputProps}
            />
          </form>
        )}
      </PopoverContent>
    </Popover>
  );
};
